import { useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ALL_CUSTOMERS, customerScope, switchScopeTarget } from '../lib/navigation.js';
import { filterCustomerOptions } from '../lib/customer-options.js';
import { useFocusTrap } from '../lib/use-focus-trap.js';

// Status dots read the portal tokens (src/styles.css); the status word is always beside them.
const DOT = { green: 'bg-ipc-good', amber: 'bg-[var(--warn-dot)]', red: 'bg-ipc-bad', slate: 'bg-[#A7B4C6]' };

/**
 * A customer status dot: status only, never decoration. Never the only signal:
 * render it only next to the visible status label.
 */
export function StatusDot({ tone }) {
  if (!tone) return null;
  return <span aria-hidden="true" data-status-dot className={`inline-block h-2 w-2 shrink-0 rounded-sm ${DOT[tone] ?? DOT.slate}`} />;
}

/**
 * The scope selector of the TopBar (portal workbench spec). Operators get
 * "All customers" plus a searchable list with a status dot per customer; a
 * customer user with one tenant sees its name without a dropdown. The status
 * dot appears only in the option rows, beside the visible status label, so
 * status is never conveyed by color alone. Switching
 * keeps the current section when the target shows it, otherwise the landing
 * rule (lib/navigation.js switchScopeTarget). inline renders the list
 * without the popover, for the phone drawer.
 *
 * ENTRAID-347 redesign: the switcher is the customer's identity in the top bar (the page no longer repeats it): the
 * name, a status dot and the status word, and a chevron. `onViewAsClient` (operators, customer scope, not already
 * viewing as the client) adds the "View as client" item to the menu, reachable by keyboard.
 */
export default function CustomerSwitcher({ navClaims, scope, pathname, options, loading = false, error = null, inline = false, onNavigate, onViewAsClient = null }) {
  const current = scope?.kind === 'customer' ? options.find((o) => o.customerId === scope.customerId) ?? null : null;
  const currentLabel = scope?.kind === 'all' ? 'All customers' : current?.name ?? scope?.customerId ?? 'Choose a customer';
  const multi = navClaims?.isOperator === true || options.length > 1;
  const status = current?.statusLabel ? <CurrentStatus tone={current.tone} label={current.statusLabel} /> : null;

  if (!multi) {
    return (
      <span data-testid="customer-scope" className="flex h-9 min-w-0 items-center gap-2.5 rounded-lg bg-white/[0.08] px-3 text-sm font-semibold">
        <span className="truncate">{currentLabel}</span>
        {status}
      </span>
    );
  }

  const pickerProps = { navClaims, scope, pathname, options, loading, error };
  if (inline) {
    return (
      <div>
        <CustomerPicker {...pickerProps} onPick={() => onNavigate?.()} />
        {onViewAsClient ? <ViewAsClientItem onClick={() => { onNavigate?.(); onViewAsClient(); }} /> : null}
      </div>
    );
  }
  return <CustomerPopover pickerProps={pickerProps} currentLabel={currentLabel} status={status} onNavigate={onNavigate} onViewAsClient={onViewAsClient} />;
}

/** The current customer's status in the trigger: a dot and the word (never color alone). */
function CurrentStatus({ tone, label }) {
  return (
    <span className="flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-white/80" data-switcher-status="">
      <StatusDot tone={tone} />
      {label}
    </span>
  );
}

/** "View as client": see exactly what this client sees (operators only; UI only, the token stays the operator's). */
function ViewAsClientItem({ onClick }) {
  return (
    <div className="mt-2 border-t border-ipc-border pt-2">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm font-medium text-ipc-ink hover:bg-slate-100 focus:bg-slate-100"
      >
        <svg aria-hidden="true" className="h-4 w-4 shrink-0 text-ipc-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        View as client
      </button>
    </div>
  );
}

function CustomerPopover({ pickerProps, currentLabel, status, onNavigate, onViewAsClient }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  useFocusTrap(panelRef, { active: open, onClose: () => setOpen(false), returnFocusRef: triggerRef, initialFocusRef: searchRef });

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 max-w-[22rem] items-center gap-2.5 rounded-lg bg-white/[0.08] px-3 text-sm font-semibold hover:bg-white/15"
      >
        <span className="sr-only">Customer: </span>
        <span className="truncate">{currentLabel}</span>
        {status}
        <svg aria-hidden="true" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Choose a customer"
          className="on-light absolute left-0 z-40 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-md border border-ipc-border bg-white p-2 text-ipc-text shadow-lg"
        >
          <CustomerPicker
            {...pickerProps}
            searchRef={searchRef}
            onPick={() => {
              setOpen(false);
              onNavigate?.();
            }}
          />
          {onViewAsClient ? (
            <ViewAsClientItem
              onClick={() => {
                setOpen(false);
                onNavigate?.();
                onViewAsClient();
              }}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

/** The visible marker of the current scope: a check next to a bolder name, never color alone. */
function CurrentMark() {
  return (
    <svg aria-hidden="true" data-current-marker className="h-4 w-4 shrink-0 text-ipc-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CustomerPicker({ navClaims, scope, pathname, options, loading, error, searchRef, onPick }) {
  const [query, setQuery] = useState('');
  const inputId = useId();
  const ownSearchRef = useRef(null);
  const search = searchRef ?? ownSearchRef;
  const listRef = useRef(null);
  const shown = useMemo(() => filterCustomerOptions(options, query), [options, query]);

  function onKeyDown(event) {
    const links = Array.from(listRef.current?.querySelectorAll('a') ?? []);
    if (links.length === 0) return;
    const index = links.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      links[Math.min(index + 1, links.length - 1)].focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index <= 0) search.current?.focus();
      else links[index - 1].focus();
    } else if (event.key === 'Home' && index >= 0) {
      event.preventDefault();
      links[0].focus();
    } else if (event.key === 'End' && index >= 0) {
      event.preventDefault();
      links[links.length - 1].focus();
    }
  }

  const optionCls = 'flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-100 focus:bg-slate-100';
  const allCurrent = scope?.kind === 'all';
  return (
    <div onKeyDown={onKeyDown}>
      <label htmlFor={inputId} className="sr-only">Search customers</label>
      <input
        id={inputId}
        ref={search}
        type="search"
        autoComplete="off"
        placeholder="Search customers"
        className="input text-sm"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <ul ref={listRef} aria-label="Customers" className="mt-2 max-h-80 overflow-y-auto">
        {navClaims?.isOperator === true && (
          <li>
            <Link
              to={switchScopeTarget(navClaims, pathname, ALL_CUSTOMERS)}
              aria-current={allCurrent ? 'true' : undefined}
              onClick={onPick}
              className={`${optionCls} ${allCurrent ? 'font-semibold' : 'font-medium'}`}
            >
              <span data-option-name className="min-w-0 flex-1 truncate">All customers</span>
              {allCurrent && <CurrentMark />}
            </Link>
          </li>
        )}
        {shown.map((o) => {
          const isCurrent = scope?.kind === 'customer' && scope.customerId === o.customerId;
          return (
            <li key={o.customerId}>
              <Link
                to={switchScopeTarget(o.navClaims ?? navClaims, pathname, customerScope(o.customerId))}
                aria-current={isCurrent ? 'true' : undefined}
                onClick={onPick}
                className={`${optionCls}${isCurrent ? ' font-semibold' : ''}`}
              >
                <StatusDot tone={o.tone} />
                <span data-option-name className="min-w-0 flex-1 truncate">{o.name}</span>
                {o.statusLabel && <span className="text-xs font-normal text-slate-600">{o.statusLabel}</span>}
                {isCurrent && <CurrentMark />}
              </Link>
            </li>
          );
        })}
      </ul>
      {loading && <p className="px-2 py-1 text-sm text-slate-600">Loading customers...</p>}
      {error && <p role="alert" className="px-2 py-1 text-sm text-red-700">Could not load the customer list: {error}</p>}
      {!loading && !error && query.trim() && shown.length === 0 && (
        <p className="px-2 py-1 text-sm text-slate-600">No customer matches "{query}".</p>
      )}
    </div>
  );
}
